// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventContextAction, NoteList } from '@/audit/api/types';
import { Stack, Tooltip, Typography } from '@mui/material';

/**
 * Represents a note difference for audit display
 */
interface NoteDifference {
  id: string;
  key: string;
  content: string | null;
  isOld: boolean;
  isNew: boolean;
}

/**
 * Props for the AuditNoteListLabel component
 */
interface Props {
  property: NoteList;
  action: AuditEventContextAction;
}

/**
 * Displays note changes in the audit log
 * Shows which notes were added or removed from a project
 */
export function AuditNoteListLabel(props: Props) {
  const notes: NoteDifference[] = [];

  // Process old values (notes that existed before)
  props.property.oldValue?.forEach(({ id, key, content }) => {
    notes.push({ id, key, content, isOld: true, isNew: false });
  });

  // Process new values (notes that exist after)
  props.property.newValue?.forEach(({ id, key, content }) => {
    const note = notes.find((note) => note.id === id);

    if (note === undefined) {
      return notes.push({ id, key, content, isOld: false, isNew: true });
    }

    note.isNew = true;
  });

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {notes.length > 0 ? (
        notes.map(({ id, content, isNew, isOld }) => {
          // Skip notes that didn't change (both old and new)
          if (isNew === isOld) {
            return null;
          }

          // Handle empty content
          const isEmpty = !content || content === '';

          // Handle empty content
          const displayContent = isEmpty ? 'Empty note' : content;

          return (
            <Typography
              key={id}
              variant="caption"
              marginLeft={2}
              sx={{
                color: isEmpty ? 'text.secondary' : 'inherit',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '25rem',
              }}
            >
              <Tooltip title={content} arrow placement="top">
                <span>
                  <Typography component="span" variant="inherit">
                    {displayContent}
                  </Typography>
                </span>
              </Tooltip>

              {props.action !== 'DELETE' && (
                <>
                  {' '}
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{ textDecoration: 'underline' }}
                  >
                    {isNew ? 'added' : 'removed'}
                  </Typography>
                </>
              )}
            </Typography>
          );
        })
      ) : (
        <Typography variant="caption" marginLeft={2} color="text.secondary">
          None
        </Typography>
      )}
    </Stack>
  );
}
